import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import logger from 'src/utils/logger';
import classify from 'src/utils/classify';
import {useDropzone} from 'react-dropzone';
import {Button} from '@spaced-out/ui-design-system';
import {type Part} from 'src/components/agentic-ui';
import {ElevationBaseContext, useElevation} from './elevation';
import css from './editor.css';

/* ---------- Lexical ---------- */
import {LexicalComposer} from '@lexical/react/LexicalComposer';
import {RichTextPlugin} from '@lexical/react/LexicalRichTextPlugin';
import {HistoryPlugin} from '@lexical/react/LexicalHistoryPlugin';
import {AutoFocusPlugin} from '@lexical/react/LexicalAutoFocusPlugin';
import {ContentEditable} from '@lexical/react/LexicalContentEditable';
import {LexicalErrorBoundary} from '@lexical/react/LexicalErrorBoundary';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
} from '@lexical/react/LexicalTypeaheadMenuPlugin';
import {
  $getRoot,
  $getSelection,
  $isRangeSelection,
  $isElementNode,
  $isTextNode,
  $getNodeByKey,
  TextNode,
  DecoratorNode,
  type DOMConversionMap,
  type DOMExportOutput,
  type EditorState,
  type ElementNode,
  $createParagraphNode,
  $createTextNode,
  KEY_ENTER_COMMAND,
  KEY_ESCAPE_COMMAND,
  COMMAND_PRIORITY_HIGH,
} from 'lexical';
import {$convertToMarkdownString, TRANSFORMERS} from '@lexical/markdown';
import {OnChangePlugin} from '@lexical/react/LexicalOnChangePlugin';
import {Menu} from '@spaced-out/ui-design-system/lib/components/Menu';
import type {
  MenuOption as DSMenuOption,
  MenuGroupTitleOption,
} from '@spaced-out/ui-design-system/lib/components/Menu';

/* ===================== Types ===================== */
export type MentionUser = {id: string; label: string; avatarUrl?: string};

export type SuggestionItem = {
  key: string;
  label: string;
  iconLeft?: string;
  meta?: unknown;
};
export type SuggestionSection = {
  key: string;
  title: string;
  items: SuggestionItem[];
};
export type UploadResult = {
  id: string;
  url: string;
  name: string;
  mime: string;
  size: number;
};

type MentionData = {id: string; label: string};
type AttachmentAttrs = {
  id: string;
  name: string;
  size: number;
  mime: string;
  url?: string;
  kbLabel: string;
};

export interface EditorProps {
  /** Placeholder text — pass already-translated (this component does no i18n). */
  placeholder?: string;
  fetchMentionSuggestions?: (q: string) => Promise<MentionUser[]>;
  onUploadFiles?: (files: File[]) => Promise<UploadResult[]>;
  onSend: (parts: Part[]) => Promise<void> | void;
  className?: string;
  disableSend?: boolean;
  autoFocus?: boolean;
  showAttachmentButton?: boolean;
  /** Replaces the default bottom bar entirely (e.g. homepage search reuse). */
  renderBottomBar?: (props: {
    onSend: () => Promise<void>;
    insertMentionTrigger: () => void;
  }) => React.ReactNode;
  enableMentions?: boolean;
  enableUpload?: boolean;
  enableVoice?: boolean;
  enableSuggestions?: boolean;
  suggestions?: SuggestionSection[];
  onQueryChange?: (q: string) => void;
  onSelectSuggestion?: (item: SuggestionItem) => void;
  suggestionsLoading?: boolean;
  /** Mic affordance (button + device picker) rendered in the idle action row.
   *  Self-contained slot — the editor stays mic-agnostic for reuse. */
  micControl?: React.ReactNode;
  /** Slot rendered above the text input (e.g. a recording waveform). */
  topSlot?: React.ReactNode;
  /** Replaces the default mic/send actions in the bottom bar (e.g. ✕/✓). */
  bottomBarActions?: React.ReactNode;
  /** Recording mode: column layout, hidden text input, right-aligned actions. */
  isRecording?: boolean;
  /** Panel base z-index. Overlays (mic picker, @-mention menu) add their UDS
   *  elevation token to this so they render above the panel's stacking context. */
  elevation?: number;
}

export interface EditorHandle {
  setPlainText: (text: string) => void;
  focus: () => void;
  clear: () => void;
  /* ---- Live voice transcript (streaming STT) ---- */
  /** Snapshot the editor so cancelVoice can fully restore it. */
  beginVoice: () => void;
  /** Replace the live (faded) interim region with the current guess. */
  pushInterim: (text: string) => void;
  /** Commit a finalized chunk as solid text; clears the interim region. */
  pushFinal: (text: string) => void;
  /** Stop: drop any lingering interim text, keep the committed transcript. */
  endVoice: () => void;
  /** Cancel: restore the pre-recording snapshot (discard everything spoken). */
  cancelVoice: () => void;
}

/* ================= Lexical custom nodes ================ */
// Mention
class MentionNode extends TextNode {
  __data: MentionData;
  static override getType() {
    return 'mention';
  }
  static override clone(n: MentionNode) {
    return new MentionNode(n.__text, n.__data, n.__key);
  }
  constructor(text: string, data: MentionData, key?: string) {
    super(text, key);
    this.__data = data;
  }
  override createDOM() {
    const span = document.createElement('span');
    span.className = css.mentionChip;
    span.textContent = `@${this.__data.label}`;
    return span;
  }
  override exportJSON() {
    return {
      ...super.exportJSON(),
      type: 'mention',
      version: 1,
      data: this.__data,
    };
  }
  static override importJSON(s: any) {
    const n = new MentionNode(`@${s?.data?.label ?? ''}`, s.data);
    (n as any).setMode?.('token');
    return n;
  }
  override isInline(): true {
    return true;
  }
  override canInsertTextBefore() {
    return false;
  }
  override canInsertTextAfter() {
    return false;
  }
  override exportDOM(): DOMExportOutput {
    const el = document.createElement('span');
    el.setAttribute('data-mention', this.__data.id);
    el.textContent = `@${this.__data.label}`;
    return {element: el};
  }
  static override importDOM(): DOMConversionMap | null {
    return null;
  }
}
const $createMentionNode = (d: MentionData) => {
  const n = new MentionNode(`@${d.label}`, d);
  (n as any).setMode?.('token');
  return n;
};

// Attachment chip
class AttachmentNode extends DecoratorNode<JSX.Element> {
  __attrs: AttachmentAttrs;
  static override getType() {
    return 'attachment';
  }
  static override clone(n: AttachmentNode) {
    return new AttachmentNode(n.__attrs, n.__key);
  }
  constructor(attrs: AttachmentAttrs, key?: string) {
    super(key);
    this.__attrs = attrs;
  }
  override isInline() {
    return true;
  }
  override isIsolated() {
    return true;
  }
  override createDOM() {
    const span = document.createElement('span');
    return span;
  }
  override updateDOM() {
    return false;
  }
  override decorate() {
    const a = this.__attrs;
    const sizeKB = Math.max(1, Math.round(a.size / 1024));
    return (
      <span
        contentEditable={false}
        className={css.attachmentChip}
        title={a.mime}
      >
        <span>{a.name}</span>
        <span className={css.muted}>
          {sizeKB}
          {` ${a.kbLabel}`}
        </span>
      </span>
    );
  }
  override exportJSON() {
    return {type: 'attachment', version: 1, attrs: this.__attrs};
  }
  static override importJSON(s: any) {
    return new AttachmentNode(s.attrs);
  }
}
const $createAttachmentNode = (a: AttachmentAttrs) => new AttachmentNode(a);

// Live interim transcript — a faded TextNode replaced on each interim event and
// removed when the chunk finalizes (or the session ends).
class InterimTextNode extends TextNode {
  static override getType() {
    return 'interim';
  }
  static override clone(n: InterimTextNode) {
    return new InterimTextNode(n.__text, n.__key);
  }
  override createDOM(config: Parameters<TextNode['createDOM']>[0]) {
    const dom = super.createDOM(config);
    dom.classList.add(css.interimText);
    return dom;
  }
  override exportJSON() {
    return {...super.exportJSON(), type: 'interim', version: 1};
  }
  static override importJSON(s: any) {
    return new InterimTextNode(s.text ?? '');
  }
}
const $createInterimTextNode = (text: string) => new InterimTextNode(text);

/* ================= Editor Theme ================ */
const EditorTheme = () => ({
  paragraph: css.editorParagraph,
  text: {
    bold: css.textBold,
    italic: css.textItalic,
    underline: css.textUnderline,
    strikethrough: css.textStrikethrough,
  },
});

/* ================= Plugins ================ */
function MentionTypeahead({
  fetchMentionSuggestions,
}: {
  fetchMentionSuggestions?: (q: string) => Promise<MentionUser[]>;
}) {
  const [editor] = useLexicalComposerContext();
  class MentionOption extends MenuOption {
    id: string;
    label: string;
    constructor(user: MentionUser) {
      super(user.id);
      this.id = user.id;
      this.label = user.label;
    }
  }
  const [items, setItems] = useState<MentionOption[]>([]);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const menuElevation = useElevation('menu');
  const onQueryChange = useCallback(
    async (q: string | null) => {
      const res = (await fetchMentionSuggestions?.(q ?? '')) ?? [];
      setItems(res.slice(0, 8).map((u) => new MentionOption(u)));
    },
    [fetchMentionSuggestions],
  );

  return (
    <LexicalTypeaheadMenuPlugin<MentionOption>
      triggerFn={(text) => {
        const m = /(?:^|\s)@([\w-]{0,32})$/.exec(text);
        if (!m) {
          return null;
        }
        return {
          leadOffset: m.index + m[0].lastIndexOf('@'),
          matchingString: m[1],
          replaceableString: m[0].slice(m[0].lastIndexOf('@')),
        };
      }}
      onQueryChange={onQueryChange}
      onSelectOption={(item, nodeToReplace) => {
        editor.update(() => {
          const n = $createMentionNode({id: item.id, label: item.label});
          nodeToReplace?.replace(n);
        });
      }}
      options={items}
      onOpen={(r) => setAnchorRect(r.getRect())}
      onClose={() => setAnchorRect(null)}
      menuRenderFn={() => {
        if (!anchorRect) {
          return null;
        }
        return (
          <div
            style={{
              position: 'absolute',
              top: anchorRect.bottom + window.scrollY,
              left: anchorRect.left + window.scrollX,
              zIndex: Number(menuElevation),
            }}
            className={css.typeaheadMenu}
          >
            {items.map((u) => (
              <div
                key={u.id}
                className={css.typeaheadOption}
                onMouseDown={(e) => {
                  e.preventDefault();
                  (document.activeElement as HTMLElement)?.blur?.();
                }}
              >
                {u.label}
              </div>
            ))}
          </div>
        );
      }}
    />
  );
}

function useAttachmentPasting(
  onUploadFiles?: (files: File[]) => Promise<UploadResult[]>,
) {
  const [editor] = useLexicalComposerContext();
  return useCallback(
    async (files: File[]) => {
      await editor.update(async () => {
        const temps: AttachmentAttrs[] = files.map((f) => ({
          id: crypto.randomUUID?.() ?? `${Date.now()}-${Math.random()}`,
          name: f.name,
          size: f.size,
          mime: f.type || 'application/octet-stream',
          kbLabel: 'KB',
        }));
        const sel = $getSelection();
        temps.forEach((t, idx) => {
          sel?.insertNodes([$createAttachmentNode(t)]);
          if (idx < temps.length - 1) {
            sel?.insertText(' ');
          }
        });

        if (onUploadFiles) {
          const uploaded = await onUploadFiles(files);
          const root = $getRoot();
          const all = root.getChildren();
          all.forEach((n: any) => {
            if (n instanceof AttachmentNode) {
              n.remove();
            }
          });
          uploaded.forEach((u, idx) => {
            sel?.insertNodes([
              $createAttachmentNode({
                id: u.id,
                name: u.name,
                size: u.size,
                mime: u.mime,
                url: u.url,
                kbLabel: 'KB',
              }),
            ]);
            if (idx < uploaded.length - 1) {
              sel?.insertText(' ');
            }
          });
        }
      });
    },
    [editor, onUploadFiles],
  );
}

function KeyHandler({
  onEnter,
  onEscape,
  isSendingRef,
}: {
  onEnter: React.MutableRefObject<(() => void) | null>;
  /** Returns true when it consumed Escape (keeps rich-text from blurring). */
  onEscape: React.MutableRefObject<(() => boolean) | null>;
  isSendingRef: React.MutableRefObject<boolean>;
}) {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    // use Lexical's command system to properly intercept Enter key
    const unregisterEnter = editor.registerCommand<KeyboardEvent>(
      KEY_ENTER_COMMAND,
      (event) => {
        // Allow Shift+Enter for new lines
        if (event.shiftKey) {
          return false;
        }
        event.preventDefault();

        if (isSendingRef.current) {
          return true;
        }

        onEnter.current?.();
        return true;
      },
      COMMAND_PRIORITY_HIGH, // High priority to intercept before default behavior
    );
    // Escape must run inside Lexical's dispatch at HIGH priority — otherwise
    // @lexical/rich-text consumes it (blur) before any React handler sees it.
    const unregisterEscape = editor.registerCommand<KeyboardEvent>(
      KEY_ESCAPE_COMMAND,
      () => onEscape.current?.() ?? false,
      COMMAND_PRIORITY_HIGH,
    );
    return () => {
      unregisterEnter();
      unregisterEscape();
    };
  }, [editor, onEnter, onEscape, isSendingRef]);
  return null;
}

function AttachmentBridgePlugin({
  attachFilesRef,
  onUploadFiles,
}: {
  attachFilesRef: React.MutableRefObject<((files: File[]) => void) | null>;
  onUploadFiles?: (files: File[]) => Promise<UploadResult[]>;
}) {
  const attachFiles = useAttachmentPasting(onUploadFiles);
  useEffect(() => {
    attachFilesRef.current = (files: File[]) => void attachFiles(files);
    return () => {
      attachFilesRef.current = null;
    };
  }, [attachFiles, attachFilesRef]);
  return null;
}

function DefaultBottomBar({
  onSend,
  insertMentionTrigger,
  disableSend,
  isLoading,
  showMentionButton,
  micControl,
  bottomBarActions,
}: {
  onSend: () => Promise<void>;
  insertMentionTrigger: () => void;
  disableSend: boolean;
  isLoading: boolean;
  showMentionButton: boolean;
  micControl?: React.ReactNode;
  bottomBarActions?: React.ReactNode;
}) {
  return (
    <div className={css.editorBottomBar}>
      {/* Leading affordances (mention) belong to the idle composer only;
          recording/connecting swaps the whole action row (bottomBarActions). */}
      {showMentionButton && !bottomBarActions && (
        <div className={css.buttonGroup}>
          <Button
            type="ghost"
            size="small"
            iconLeftName="at"
            classNames={{icon: css.atIcon}}
            onClick={insertMentionTrigger}
          />
        </div>
      )}
      {bottomBarActions ? (
        <div className={css.bottomBarActions}>{bottomBarActions}</div>
      ) : (
        <div className={css.actionGroup}>
          {micControl && <div className={css.micCluster}>{micControl}</div>}
          <Button
            size="small"
            disabled={disableSend}
            isLoading={isLoading}
            type="gradient"
            iconLeftName="send"
            iconLeftType="solid"
            onClick={() => void onSend()}
          ></Button>
        </div>
      )}
    </div>
  );
}

/* ================= Suggestions Plugin ================ */
function SuggestionsPlugin({
  enabled,
  suggestions = [],
  dismissed = false,
  onQueryChange,
  onSelectSuggestion,
}: {
  enabled: boolean;
  suggestions?: SuggestionSection[];
  /** Hides the menu without clearing it (e.g. Escape); reset by the parent. */
  dismissed?: boolean;
  onQueryChange?: (q: string) => void;
  onSelectSuggestion?: (item: SuggestionItem) => void;
}) {
  const handleChange = useCallback(
    (editorState: EditorState) => {
      if (!enabled || !onQueryChange) {
        return;
      }
      editorState.read(() => {
        onQueryChange($getRoot().getTextContent());
      });
    },
    [enabled, onQueryChange],
  );

  const isMenuOpen = enabled && suggestions.length > 0 && !dismissed;

  // Flat key->item lookup so Menu's onSelect can resolve the chosen item.
  const itemByKey = useMemo(() => {
    const map = new Map<string, SuggestionItem>();
    suggestions.forEach((s) => s.items.forEach((i) => map.set(i.key, i)));
    return map;
  }, [suggestions]);

  const groupTitleOptions: MenuGroupTitleOption[] = useMemo(
    () =>
      suggestions.map((s) => ({
        groupTitle: s.title,
        options: s.items.map(
          (i): DSMenuOption => ({
            key: i.key,
            label: i.label,
            iconLeft: i.iconLeft,
          }),
        ),
      })),
    [suggestions],
  );

  if (!enabled) {
    return null;
  }
  return (
    <>
      <OnChangePlugin onChange={handleChange} ignoreSelectionChange />
      {isMenuOpen && (
        <div className={css.suggestionsMenu}>
          <Menu
            groupTitleOptions={groupTitleOptions}
            onSelect={(option) => {
              const item = itemByKey.get(option.key);
              if (item) {
                onSelectSuggestion?.(item);
              }
            }}
          />
        </div>
      )}
    </>
  );
}

/* ================= Main Editor Component ================ */
export const Editor = forwardRef<EditorHandle, EditorProps>(
  (
    {
      placeholder,
      showAttachmentButton = false,
      enableMentions = true,
      enableUpload = true,
      enableVoice: _enableVoice = false,
      fetchMentionSuggestions,
      onUploadFiles,
      onSend,
      className,
      renderBottomBar,
      disableSend = false,
      autoFocus = false,
      enableSuggestions = false,
      suggestions,
      onQueryChange,
      onSelectSuggestion,
      suggestionsLoading: _suggestionsLoading = false,
      micControl,
      topSlot,
      bottomBarActions,
      isRecording = false,
      elevation = 0,
    },
    ref,
  ) => {
    // Mentions need both an explicit opt-in and a suggestions fetcher.
    const mentionsEnabled = enableMentions && !!fetchMentionSuggestions;
    const initialConfig = useMemo(
      () => ({
        namespace: 'chat-composer',
        onError: (e: unknown) => {
          // eslint-disable-next-line no-console
          console.error(e);
        },
        theme: EditorTheme(),
        nodes: [MentionNode, AttachmentNode, InterimTextNode],
      }),
      [],
    );
    const attachFilesRef = useRef<((files: File[]) => void) | null>(null);
    const {getRootProps, getInputProps, isDragActive} = useDropzone({
      multiple: true,
      noClick: true,
      onDrop: (files) => void attachFilesRef.current?.(files),
    });
    const onEnter = useRef<(() => void) | null>(null);
    const onEscape = useRef<(() => boolean) | null>(null);

    const [editor, setEditor] = useState<any>(null);
    const [isEditorEmpty, setIsEditorEmpty] = useState(true);
    // Escape hides the suggestions menu; the next edit brings it back.
    const [suggestionsDismissed, setSuggestionsDismissed] = useState(false);
    // Content taller than one line switches the compact single-row composer
    // to the two-row layout (text above a full-width action footer).
    const [isMultiline, setIsMultiline] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const isSendingRef = useRef(false);

    // Live voice transcript bookkeeping.
    const voiceSnapshotRef = useRef<EditorState | null>(null);
    const interimKeyRef = useRef<string | null>(null);
    const interimPrefixRef = useRef('');

    // Helper function to check if editor content is empty
    const checkIfEditorEmpty = useCallback(() => {
      if (!editor) {
        return true;
      }
      let isEmpty = true;
      editor.getEditorState().read(() => {
        const root = $getRoot();
        const md = $convertToMarkdownString(TRANSFORMERS);
        const nodes = root.getChildren();
        let hasAttachments = false;
        nodes.forEach((n: any) => {
          if (n instanceof AttachmentNode) {
            hasAttachments = true;
          }
        });
        isEmpty = !md.trim() && !hasAttachments;
      });
      return isEmpty;
    }, [editor]);

    // Editing the query un-dismisses the suggestions menu.
    const handleSuggestionsQueryChange = useCallback(
      (q: string) => {
        setSuggestionsDismissed(false);
        onQueryChange?.(q);
      },
      [onQueryChange],
    );

    // Ref (not state) so KeyHandler's command registration never re-subscribes;
    // reassigned every render to see fresh props/state.
    onEscape.current = () => {
      const isMenuOpen =
        enableSuggestions &&
        (suggestions?.length ?? 0) > 0 &&
        !suggestionsDismissed;
      if (!isMenuOpen) {
        return false;
      }
      setSuggestionsDismissed(true);
      return true;
    };

    const insertMentionTrigger = useCallback(() => {
      if (!editor) {
        return;
      }
      editor.update(() => {
        const sel = $getSelection();
        if (!$isRangeSelection(sel)) {
          return;
        }
        try {
          const anchorNode = sel.anchor.getNode();
          let needsSpace = true;
          if (anchorNode instanceof TextNode) {
            const text = anchorNode.getTextContent();
            const idx = sel.anchor.offset - 1;
            if (idx < 0) {
              needsSpace = false; // at start of node
            } else {
              const ch = text[idx];
              needsSpace = ch !== ' ' && ch !== '\n' && ch !== '\t';
            }
          }
          sel.insertText(needsSpace ? ' @' : '@');
        } finally {
          // ensure focus so typeahead can appear
          editor.focus();
        }
      });
    }, [editor]);

    const extractAndSend = useCallback(async () => {
      if (!editor) {
        return;
      }
      // prevent duplicate sends - check ref synchronously before state update
      if (isSendingRef.current) {
        return;
      }
      isSendingRef.current = true;
      setIsSending(true);

      let text = '';
      const attachments: AttachmentAttrs[] = [];
      editor.getEditorState().read(() => {
        const root = $getRoot();
        // get plain text content directly without markdown conversion
        text = root.getTextContent();
        const nodes = root.getChildren();
        nodes.forEach((n: any) => {
          if (n instanceof AttachmentNode) {
            attachments.push((n as AttachmentNode).__attrs);
          }
        });
      });
      const parts: Part[] = [];
      if (text.trim()) {
        parts.push({type: 'plain_text', data: text.trim()});
      }
      if (attachments.length) {
        // parts.push({type: 'widget', data: {attachments}});
      }

      // if no content to send, reset flag and return early
      if (parts.length === 0) {
        isSendingRef.current = false;
        setIsSending(false);
        return;
      }

      try {
        await onSend(parts);
        editor.update(() => {
          $getRoot().clear();
        });
      } catch (error) {
        logger.error('Error sending editor content', error);
      } finally {
        isSendingRef.current = false;
        setIsSending(false);
      }
    }, [editor, onSend]);

    // Setup onEnter callback
    useEffect(() => {
      onEnter.current = () => {
        if (disableSend) {
          return;
        }
        void extractAndSend();
      };
      return () => {
        onEnter.current = null;
      };
    }, [extractAndSend, disableSend]);

    // Track editor content changes
    useEffect(() => {
      if (!editor) {
        return;
      }
      // Update listeners fire post-reconcile, so scrollHeight is fresh; 1.75
      // line-heights sits between the 32px one-line floor and two wrapped lines.
      const checkMultiline = () => {
        const el = editor.getRootElement() as HTMLElement | null;
        if (!el) {
          return false;
        }
        const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 20;
        return el.scrollHeight > lineHeight * 1.75;
      };
      // Latch: expansion widens the text area, so re-measuring oscillates.
      // Expand on first wrap; collapse only when the editor empties.
      const syncLayoutState = () => {
        const empty = checkIfEditorEmpty();
        setIsEditorEmpty(empty);
        setIsMultiline((prev) => !empty && (prev || checkMultiline()));
      };
      syncLayoutState();

      const removeListener = editor.registerUpdateListener(syncLayoutState);

      return () => {
        removeListener();
      };
    }, [editor, checkIfEditorEmpty]);

    // Plugin to capture editor instance
    function EditorRefPlugin() {
      const [editorInstance] = useLexicalComposerContext();
      useEffect(() => {
        setEditor(editorInstance);
      }, [editorInstance]);
      return null;
    }

    useImperativeHandle(
      ref,
      () => ({
        setPlainText: (text: string) => {
          if (!editor) {
            return;
          }
          editor.update(() => {
            const root = $getRoot();
            root.clear();
            const paragraph = $createParagraphNode();
            paragraph.append($createTextNode(text));
            root.append(paragraph);
          });
          editor.focus();
        },
        focus: () => editor?.focus?.(),
        clear: () => {
          if (!editor) {
            return;
          }
          editor.update(() => {
            $getRoot().clear();
          });
        },
        beginVoice: () => {
          if (!editor) {
            return;
          }
          voiceSnapshotRef.current = editor.getEditorState();
          interimKeyRef.current = null;
          interimPrefixRef.current = '';
          editor.focus();
        },
        pushInterim: (text: string) => {
          if (!editor) {
            return;
          }
          editor.update(() => {
            const existingKey = interimKeyRef.current;
            const node = existingKey ? $getNodeByKey(existingKey) : null;
            if (!text) {
              if (node) {
                node.remove();
              }
              interimKeyRef.current = null;
              return;
            }
            if (node && $isTextNode(node)) {
              node.setTextContent(interimPrefixRef.current + text);
              return;
            }
            const root = $getRoot();
            const last = root.getLastChild();
            let para: ElementNode;
            if ($isElementNode(last)) {
              para = last;
            } else {
              para = $createParagraphNode();
              root.append(para);
            }
            const existing = root.getTextContent();
            interimPrefixRef.current =
              existing.length > 0 && !/\s$/.test(existing) ? ' ' : '';
            const interimNode = $createInterimTextNode(
              interimPrefixRef.current + text,
            );
            para.append(interimNode);
            interimKeyRef.current = interimNode.getKey();
          });
        },
        pushFinal: (text: string) => {
          if (!editor || !text.trim()) {
            return;
          }
          editor.update(() => {
            if (interimKeyRef.current) {
              $getNodeByKey(interimKeyRef.current)?.remove();
              interimKeyRef.current = null;
            }
            const root = $getRoot();
            const last = root.getLastChild();
            let para: ElementNode;
            if ($isElementNode(last)) {
              para = last;
            } else {
              para = $createParagraphNode();
              root.append(para);
            }
            const existing = root.getTextContent();
            const needsSpace = existing.length > 0 && !/\s$/.test(existing);
            const node = $createTextNode((needsSpace ? ' ' : '') + text.trim());
            para.append(node);
            node.selectEnd();
          });
        },
        endVoice: () => {
          voiceSnapshotRef.current = null;
          if (!editor) {
            return;
          }
          editor.update(() => {
            if (interimKeyRef.current) {
              $getNodeByKey(interimKeyRef.current)?.remove();
              interimKeyRef.current = null;
            }
          });
          interimPrefixRef.current = '';
        },
        cancelVoice: () => {
          const snapshot = voiceSnapshotRef.current;
          voiceSnapshotRef.current = null;
          interimKeyRef.current = null;
          interimPrefixRef.current = '';
          if (editor && snapshot) {
            editor.setEditorState(snapshot);
          }
        },
      }),
      [editor],
    );

    return (
      <ElevationBaseContext.Provider value={elevation}>
        <div
          {...getRootProps()}
          className={classify(css.root, {
            [css.dragActive]: isDragActive,
            [css.editorContainerWithoutAttachmentButton]: !showAttachmentButton,
            [css.multiline]: isMultiline,
            [css.recording]: isRecording,
            [className || '']: !!className,
          })}
          tabIndex={-1}
          onClick={() => editor?.focus()}
          onKeyDown={(e) => {
            if (enableSuggestions && e.key === 'Escape') {
              setSuggestionsDismissed(true);
            }
          }}
          onBlur={(e) => {
            // Combobox behavior: focus leaving the composer closes the menu
            // (tab out, click outside, or an extension blurring on Escape).
            if (
              enableSuggestions &&
              !e.currentTarget.contains(e.relatedTarget as Node | null)
            ) {
              setSuggestionsDismissed(true);
            }
          }}
        >
          <LexicalComposer initialConfig={initialConfig}>
            <div className={css.editorContainer}>
              {topSlot ? <div className={css.topSlot}>{topSlot}</div> : null}
              <RichTextPlugin
                contentEditable={
                  <ContentEditable
                    className={css.contentEditable}
                    aria-placeholder={placeholder ?? ''}
                    placeholder={
                      <div className={css.editorPlaceholder}>{placeholder}</div>
                    }
                  />
                }
                ErrorBoundary={LexicalErrorBoundary}
              />
              <HistoryPlugin />
              {autoFocus && <AutoFocusPlugin />}
              {/* @-mentions are gated on a suggestions fetcher — omit it and the
                feature (button + typeahead) stays hidden until it's built. */}
              {mentionsEnabled && (
                <MentionTypeahead
                  fetchMentionSuggestions={fetchMentionSuggestions}
                />
              )}
              <KeyHandler
                onEnter={onEnter}
                onEscape={onEscape}
                isSendingRef={isSendingRef}
              />
              {enableUpload && (
                <AttachmentBridgePlugin
                  attachFilesRef={attachFilesRef}
                  onUploadFiles={onUploadFiles}
                />
              )}
              <EditorRefPlugin />
              <SuggestionsPlugin
                enabled={enableSuggestions}
                suggestions={suggestions}
                dismissed={suggestionsDismissed}
                onQueryChange={handleSuggestionsQueryChange}
                onSelectSuggestion={onSelectSuggestion}
              />
              {renderBottomBar ? (
                renderBottomBar({onSend: extractAndSend, insertMentionTrigger})
              ) : (
                <DefaultBottomBar
                  onSend={extractAndSend}
                  insertMentionTrigger={insertMentionTrigger}
                  disableSend={isEditorEmpty || disableSend || isSending}
                  isLoading={isSending}
                  showMentionButton={mentionsEnabled}
                  micControl={micControl}
                  bottomBarActions={bottomBarActions}
                />
              )}
            </div>
          </LexicalComposer>
          <input {...getInputProps()} hidden />
        </div>
      </ElevationBaseContext.Provider>
    );
  },
);
